import { type CreateHotelRequest, type HotelDetail, type HotelPhoto } from '@utazzo/shared';
import {
  UzAlert,
  UzButton,
  UzDateTimePicker,
  UzForm,
  UzFormField,
  UzInput,
  UzNumberInput,
  UzSelect,
  UzTabs,
  UzTextarea,
  UzTooltip,
} from '@utazzo/ui';
import { ArrowLeft, Sparkles } from 'lucide-react';
import { type ReactNode, useEffect, useMemo, useState } from 'react';
import { useForm, type UseFormReturn } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';

import { AmenityGrid } from '../components/AmenityGrid';
import { HotelSetupCard } from '../components/HotelOverviewTab';
import { HotelPhotosFields } from '../components/HotelPhotosCard';
import { RoomsRatesTab } from '../components/RoomsRatesTab';
import {
  useApproveHotel,
  useCreateHotel,
  useHotelDetail,
  useHotelInventory,
  useUpdateHotel,
} from '../use-hotels';

import { CityPicker, CountryPicker, SupplierPicker } from '@/components/pickers';
import { CatalogEditorSkeleton } from '@/components/skeletons';
import { StarRatingInput } from '@/components/star-rating';
import { useCurrentUser } from '@/features/auth/use-auth';
import { RichTextEditor } from '@/features/website-cms/components/RichTextEditor';
import { useListReturn } from '@/hooks/use-list-return';
import { useSaveShortcut } from '@/hooks/use-save-shortcut';

/**
 * Hotel editor (Shopify-style catalog layout).
 *
 * Two tabs:
 *   Details       main column (identity, location, description, media,
 *                 policies) + right sidebar (setup checklist, status &
 *                 approval, commercial, TBO). One scroll, no tab-hunting.
 *   Rooms & rates rooms with their season rates nested inside.
 *
 * Routes:
 *   /hotels/new          create (Details only until saved)
 *   /hotels/:id/edit     edit
 */

const STATUS_OPTIONS = [
  { value: 'DRAFT', label: 'Draft' },
  { value: 'PENDING_APPROVAL', label: 'Pending approval' },
  { value: 'ACTIVE', label: 'Active' },
  { value: 'INACTIVE', label: 'Inactive' },
  { value: 'ARCHIVED', label: 'Archived' },
];

const MARKUP_OPTIONS = [
  { value: '', label: 'No markup' },
  { value: 'PERCENT', label: 'Percent' },
  { value: 'FLAT', label: 'Flat amount' },
];

interface HotelFormValues {
  name: string;
  slug: string | null;
  starRating: number;
  hotelChain: string | null;
  brand: string | null;
  countryId: string | null;
  cityId: string | null;
  address: string | null;
  neighbourhood: string | null;
  latitude: number | null;
  longitude: number | null;
  shortDescription: string | null;
  longDescriptionHtml: string | null;
  amenities: string[];
  photos: HotelPhoto[];
  checkInTime: string | null;
  checkOutTime: string | null;
  policiesHtml: string | null;
  vendorSupplierId: string | null;
  contractRef: string | null;
  markupKind: 'PERCENT' | 'FLAT' | null;
  markupValue: number;
  tboHotelCode: string | null;
  status: HotelDetail['status'];
}

const CREATE_DEFAULTS: HotelFormValues = {
  name: '',
  slug: null,
  starRating: 0,
  hotelChain: null,
  brand: null,
  countryId: null,
  cityId: null,
  address: null,
  neighbourhood: null,
  latitude: null,
  longitude: null,
  shortDescription: null,
  longDescriptionHtml: null,
  amenities: [],
  photos: [],
  checkInTime: null,
  checkOutTime: null,
  policiesHtml: null,
  vendorSupplierId: null,
  contractRef: null,
  markupKind: null,
  markupValue: 0,
  tboHotelCode: null,
  status: 'DRAFT',
};

const parseAmenities = (a: unknown): string[] =>
  Array.isArray(a) ? a.filter((x): x is string => typeof x === 'string') : [];

const parsePhotos = (a: unknown): HotelPhoto[] => {
  if (!Array.isArray(a)) return [];
  return a.filter(
    (p): p is HotelPhoto =>
      Boolean(p) &&
      typeof p === 'object' &&
      (typeof (p as { url?: unknown }).url === 'string' ||
        typeof (p as { mediaId?: unknown }).mediaId === 'string'),
  );
};

const trimOrNull = (v: unknown): string | null => {
  if (typeof v !== 'string') return null;
  const t = v.trim();
  return t.length === 0 ? null : t;
};

/** URL slug from free text: lowercase, alphanumerics, hyphen-separated. */
const slugify = (s: string): string =>
  s
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

/** "HH:mm" -> a Date carrying only that time-of-day (for UzDateTimePicker). */
const hhmmToDate = (hhmm: string | null | undefined): Date | null => {
  if (!hhmm) return null;
  const [hStr, mStr] = hhmm.split(':');
  const h = Number(hStr);
  const m = Number(mStr);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
  const d = new Date();
  d.setHours(h, m, 0, 0);
  return d;
};

/** Date -> "HH:mm" (24-hour, zero-padded). */
const dateToHhmm = (d: Date | null): string | null =>
  d ? `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}` : null;

const toFormValues = (hotel: HotelDetail | null): HotelFormValues => {
  if (!hotel) return { ...CREATE_DEFAULTS };
  return {
    name: hotel.name,
    slug: hotel.slug,
    starRating: hotel.starRating,
    hotelChain: hotel.hotelChain,
    brand: hotel.brand,
    countryId: hotel.countryId,
    cityId: hotel.cityId,
    address: hotel.address,
    neighbourhood: hotel.neighbourhood,
    latitude: hotel.latitude,
    longitude: hotel.longitude,
    shortDescription: hotel.shortDescription,
    longDescriptionHtml: hotel.longDescriptionHtml,
    amenities: parseAmenities(hotel.amenities),
    photos: parsePhotos(hotel.photos),
    checkInTime: hotel.checkInTime,
    checkOutTime: hotel.checkOutTime,
    policiesHtml: hotel.policiesHtml,
    vendorSupplierId: hotel.vendorSupplierId,
    contractRef: hotel.contractRef,
    markupKind: hotel.markupKind,
    markupValue: hotel.markupValue,
    tboHotelCode: hotel.tboHotelCode,
    status: hotel.status,
  };
};

const fromFormValues = (values: HotelFormValues): CreateHotelRequest => ({
  name: values.name.trim(),
  slug: trimOrNull(values.slug),
  starRating: values.starRating,
  hotelChain: trimOrNull(values.hotelChain),
  brand: trimOrNull(values.brand),
  countryId: values.countryId ?? null,
  cityId: values.cityId ?? null,
  address: trimOrNull(values.address),
  neighbourhood: trimOrNull(values.neighbourhood),
  latitude: values.latitude,
  longitude: values.longitude,
  shortDescription: trimOrNull(values.shortDescription),
  longDescriptionHtml: trimOrNull(values.longDescriptionHtml),
  amenities: values.amenities.length > 0 ? values.amenities : null,
  photos: values.photos.length > 0 ? values.photos : null,
  checkInTime: trimOrNull(values.checkInTime),
  checkOutTime: trimOrNull(values.checkOutTime),
  policiesHtml: trimOrNull(values.policiesHtml),
  vendorSupplierId: values.vendorSupplierId ?? null,
  contractRef: trimOrNull(values.contractRef),
  markupKind: values.markupKind,
  markupValue: values.markupValue ?? 0,
  tboHotelCode: trimOrNull(values.tboHotelCode),
  status: values.status,
});

/** Shopify-style card: title header + stacked fields. */
const Card = ({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: ReactNode;
}): React.JSX.Element => (
  <section className="rounded-lg border border-zinc-200 bg-white">
    <header className="border-b border-zinc-100 px-5 py-3.5">
      <h3 className="text-sm font-semibold tracking-tight text-zinc-900">{title}</h3>
      {description && <p className="mt-0.5 text-xs text-zinc-500">{description}</p>}
    </header>
    <div className="space-y-5 px-5 py-5">{children}</div>
  </section>
);

export const HotelEditPage = (): React.JSX.Element => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const backToList = useListReturn('/hotels');
  const me = useCurrentUser();
  const canApprove = me?.role === 'ADMIN';
  const isCreate = !id || id === 'new';
  const [activeTab, setActiveTab] = useState<string>('details');

  const detailQuery = useHotelDetail(isCreate ? null : (id ?? null));
  const hotel = detailQuery.data ?? null;
  const inventoryQuery = useHotelInventory(isCreate ? null : (id ?? null), {
    page: 1,
    pageSize: 100,
    sortDir: 'asc',
  });
  const inventory = inventoryQuery.data?.data ?? [];

  const createHotel = useCreateHotel();
  const updateHotel = useUpdateHotel();
  const approveHotel = useApproveHotel();
  const pending = createHotel.isPending || updateHotel.isPending || approveHotel.isPending;

  const form = useForm<HotelFormValues>({ defaultValues: toFormValues(null) });

  useEffect(() => {
    if (isCreate) form.reset(toFormValues(null));
    else if (hotel) form.reset(toFormValues(hotel));
  }, [isCreate, hotel, form]);

  const submit = form.handleSubmit(async (values) => {
    // Slug is mandatory. Operators can auto-generate it from the name with the
    // wand button, but it must be set before we save.
    if (!trimOrNull(values.slug)) {
      form.setError('slug', { type: 'required', message: 'URL slug is required' });
      setActiveTab('details');
      return;
    }
    const payload = fromFormValues(values);
    if (isCreate) {
      const created = await createHotel.mutateAsync(payload);
      navigate(`/hotels/${created.id}/edit`);
    } else if (hotel) {
      await updateHotel.mutateAsync({ id: hotel.id, payload });
    }
  });

  // Cmd/Ctrl+S saves (validates + highlights errors, same as the Save button).
  useSaveShortcut(() => {
    if (!pending) void submit();
  });

  const tabs = useMemo(() => {
    const list: { value: string; label: ReactNode; content: ReactNode; disabled?: boolean }[] = [
      {
        value: 'details',
        label: 'Details',
        content: (
          <UzForm form={form} onSubmit={submit}>
            <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
              {/* Main column - core identity only. Content/media live on
                  their own tab to keep this scannable. */}
              <div className="min-w-0 space-y-6">
                <IdentityCard form={form} pending={pending} />
                <LocationCard form={form} pending={pending} />
                <PoliciesCard pending={pending} />
              </div>

              {/* Sidebar - sticks just below the top bar on scroll. */}
              <aside className="space-y-6 self-start lg:sticky lg:top-8">
                {!isCreate && hotel && (
                  <HotelSetupCard
                    hotel={hotel}
                    inventory={inventory}
                    inventoryLoading={inventoryQuery.isLoading}
                    onGoToRooms={() => setActiveTab('rooms')}
                  />
                )}
                <StatusCard pending={pending} canApprove={canApprove} />
                <CommercialCard form={form} pending={pending} />
                <ConnectionsCard pending={pending} hotel={hotel} />
              </aside>
            </div>
          </UzForm>
        ),
      },
      {
        value: 'content',
        label: 'Content & media',
        content: (
          <UzForm form={form} onSubmit={submit}>
            <div className="mx-auto max-w-3xl space-y-6">
              <DescriptionCard pending={pending} />
              <MediaCard pending={pending} />
            </div>
          </UzForm>
        ),
      },
    ];
    // Always show the Rooms tab; disabled until the hotel exists (you can't
    // attach rooms to an unsaved hotel). Tooltip nudges the agent to save first.
    const roomsLocked = isCreate || !hotel;
    list.push({
      value: 'rooms',
      disabled: roomsLocked,
      label: (
        <span
          className="inline-flex items-center gap-1.5"
          title={roomsLocked ? 'Add and save the hotel details first to manage rooms.' : undefined}
        >
          Rooms & rates
          {hotel && hotel.rooms.length > 0 && (
            <span className="text-[10px] text-zinc-400 tabular-nums">{hotel.rooms.length}</span>
          )}
        </span>
      ),
      content:
        !isCreate && hotel ? (
          <RoomsRatesTab
            hotelId={hotel.id}
            rooms={hotel.rooms}
            inventory={inventory}
            inventoryLoading={inventoryQuery.isLoading}
            disabled={pending}
          />
        ) : (
          <UzAlert tone="info">
            Save the hotel details first, then add room types & rates here.
          </UzAlert>
        ),
    });
    return list;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- submit is stable per render cycle
  }, [
    isCreate,
    hotel,
    inventory,
    inventoryQuery.isLoading,
    canApprove,
    form,
    pending,
    approveHotel,
  ]);

  const statusDot =
    hotel?.status === 'ACTIVE'
      ? 'bg-emerald-500'
      : hotel?.status === 'DRAFT' || hotel?.status === 'PENDING_APPROVAL'
        ? 'bg-amber-500'
        : 'bg-zinc-400';
  const statusText =
    hotel?.status === 'ACTIVE'
      ? 'Active'
      : hotel?.status === 'DRAFT'
        ? 'Draft'
        : hotel?.status === 'PENDING_APPROVAL'
          ? 'Pending approval'
          : hotel?.status === 'INACTIVE'
            ? 'Inactive'
            : hotel?.status === 'ARCHIVED'
              ? 'Archived'
              : '';

  return (
    <div className="-mx-4 -mt-6 flex min-h-full flex-col lg:-mx-8 lg:-mt-8">
      {/* Sticky top bar - breaks out of the scroll container's padding so it sits
          flush under the app header and stays solid (content never shows through). */}
      <div className="sticky -top-6 z-30 flex h-12 items-center gap-3 border-b border-zinc-200 bg-white px-6 lg:-top-8 lg:px-8">
        <button
          type="button"
          onClick={backToList}
          className="-ml-1 inline-flex h-7 w-7 items-center justify-center rounded text-zinc-500 transition-colors hover:bg-zinc-100 hover:text-zinc-900"
          aria-label="Back to hotels"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
        <div className="flex min-w-0 items-center gap-2">
          <span className="text-xs font-medium text-zinc-500">Hotels</span>
          <span className="text-zinc-300" aria-hidden>
            /
          </span>
          <span className="truncate text-sm font-semibold tracking-tight text-zinc-900">
            {isCreate ? 'Add hotel' : hotel ? hotel.name : 'Edit'}
          </span>
        </div>
        <span className="flex-1" />
        {!isCreate && hotel && (
          <span className="inline-flex items-center gap-1.5 text-xs text-zinc-600">
            <span className={'h-1.5 w-1.5 rounded-full ' + statusDot} aria-hidden />
            {statusText}
          </span>
        )}
        <UzButton variant="ghost" size="sm" onClick={backToList} disabled={pending}>
          Cancel
        </UzButton>
        <UzButton size="sm" onClick={submit} loading={pending}>
          <span>{isCreate ? 'Create' : 'Save'}</span>
          <kbd className="ml-2 hidden h-4 items-center rounded border border-zinc-300/60 bg-white/15 px-1 font-mono text-[10px] text-current/80 sm:inline-flex">
            ⌘S
          </kbd>
        </UzButton>
      </div>

      <div className="mx-auto w-full max-w-6xl min-w-0 px-6 py-6 lg:px-8">
        {!isCreate && detailQuery.isLoading && <CatalogEditorSkeleton />}
        {!isCreate && detailQuery.isError && (
          <UzAlert tone="destructive">Failed to load hotel detail. Go back and retry.</UzAlert>
        )}

        {(isCreate || hotel) && (
          <UzTabs value={activeTab} onValueChange={setActiveTab} tabs={tabs} />
        )}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main column cards
// ---------------------------------------------------------------------------

interface CardProps {
  pending: boolean;
}
interface FormCardProps extends CardProps {
  form: UseFormReturn<HotelFormValues>;
}

const IdentityCard = ({ form, pending }: FormCardProps): React.JSX.Element => (
  <Card title="Identity">
    <UzFormField name="name">
      {({ field, error }) => (
        <UzInput
          {...field}
          value={(field.value as string | undefined) ?? ''}
          label="Hotel name"
          placeholder="e.g. Taj Lake Palace"
          error={error}
          required
          disabled={pending}
        />
      )}
    </UzFormField>
    <div className="grid gap-5 sm:grid-cols-2">
      <UzFormField name="starRating">
        {({ field }) => (
          <StarRatingInput
            value={(field.value as number) ?? 0}
            onChange={(v) => field.onChange(v)}
            disabled={pending}
          />
        )}
      </UzFormField>
      <UzFormField name="slug">
        {({ field, error }) => (
          <UzInput
            {...field}
            value={(field.value as string | null | undefined) ?? ''}
            onChange={(e) => field.onChange(e.target.value === '' ? null : e.target.value)}
            label="URL slug"
            required
            error={error}
            disabled={pending}
            rightAdornment={
              <UzTooltip label="Auto-generate slug">
                <button
                  type="button"
                  disabled={pending}
                  aria-label="Auto-generate slug"
                  onClick={() => {
                    const name = (form.getValues('name') ?? '').trim();
                    if (name) field.onChange(slugify(name));
                  }}
                  className="text-zinc-400 transition-colors hover:text-zinc-900 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Sparkles className="h-4 w-4" />
                </button>
              </UzTooltip>
            }
          />
        )}
      </UzFormField>
      <UzFormField name="hotelChain">
        {({ field, error }) => (
          <UzInput
            {...field}
            value={(field.value as string | null | undefined) ?? ''}
            onChange={(e) => field.onChange(e.target.value === '' ? null : e.target.value)}
            label="Chain"
            placeholder="e.g. Taj, Marriott"
            error={error}
            disabled={pending}
          />
        )}
      </UzFormField>
      <UzFormField name="brand">
        {({ field, error }) => (
          <UzInput
            {...field}
            value={(field.value as string | null | undefined) ?? ''}
            onChange={(e) => field.onChange(e.target.value === '' ? null : e.target.value)}
            label="Brand"
            placeholder="e.g. Vivanta, Courtyard"
            error={error}
            disabled={pending}
          />
        )}
      </UzFormField>
    </div>
  </Card>
);

const LocationCard = ({ form, pending }: FormCardProps): React.JSX.Element => {
  const countryId = form.watch('countryId');
  return (
    <Card title="Location">
      <div className="grid gap-5 sm:grid-cols-2">
        <UzFormField name="countryId">
          {({ field, error }) => (
            <CountryPicker
              value={(field.value as string | null | undefined) ?? null}
              onValueChange={(v) => {
                field.onChange(v);
                if (v !== countryId) form.setValue('cityId', null);
              }}
              disabled={pending}
              error={error}
            />
          )}
        </UzFormField>
        <UzFormField name="cityId">
          {({ field, error }) => (
            <CityPicker
              value={(field.value as string | null | undefined) ?? null}
              onValueChange={(v) => field.onChange(v)}
              countryId={countryId ?? null}
              disabled={pending}
              error={error}
            />
          )}
        </UzFormField>
      </div>
      <UzFormField name="address">
        {({ field, error }) => (
          <UzInput
            {...field}
            value={(field.value as string | null | undefined) ?? ''}
            onChange={(e) => field.onChange(e.target.value === '' ? null : e.target.value)}
            label="Street address"
            error={error}
            disabled={pending}
          />
        )}
      </UzFormField>
      <div className="grid gap-5 sm:grid-cols-3">
        <UzFormField name="neighbourhood">
          {({ field, error }) => (
            <UzInput
              {...field}
              value={(field.value as string | null | undefined) ?? ''}
              onChange={(e) => field.onChange(e.target.value === '' ? null : e.target.value)}
              label="Neighbourhood"
              placeholder="e.g. South Goa"
              error={error}
              disabled={pending}
            />
          )}
        </UzFormField>
        <UzFormField name="latitude">
          {({ field, error }) => (
            <UzNumberInput
              name={field.name}
              label="Latitude"
              precision={6}
              value={
                field.value === null || field.value === undefined ? '' : (field.value as number)
              }
              onValueChange={(v) => field.onChange(v ?? null)}
              error={error}
              disabled={pending}
            />
          )}
        </UzFormField>
        <UzFormField name="longitude">
          {({ field, error }) => (
            <UzNumberInput
              name={field.name}
              label="Longitude"
              precision={6}
              value={
                field.value === null || field.value === undefined ? '' : (field.value as number)
              }
              onValueChange={(v) => field.onChange(v ?? null)}
              error={error}
              disabled={pending}
            />
          )}
        </UzFormField>
      </div>
    </Card>
  );
};

const DescriptionCard = ({ pending }: CardProps): React.JSX.Element => (
  <Card title="Description">
    <UzFormField name="shortDescription">
      {({ field, error }) => (
        <UzTextarea
          {...field}
          value={(field.value as string | null | undefined) ?? ''}
          onChange={(e) => field.onChange(e.target.value === '' ? null : e.target.value)}
          label="Short description"
          placeholder="One-sentence summary for picker cards."
          rows={2}
          error={error}
          disabled={pending}
        />
      )}
    </UzFormField>
    <UzFormField name="longDescriptionHtml">
      {({ field }) => (
        <RichTextEditor
          label="Long description"
          placeholder="Multi-paragraph description with formatting..."
          value={(field.value as string | null | undefined) ?? ''}
          onChange={(html) => field.onChange(html === '' ? null : html)}
          disabled={pending}
        />
      )}
    </UzFormField>
  </Card>
);

const MediaCard = ({ pending }: CardProps): React.JSX.Element => (
  <>
    <Card
      title="Amenities"
      description="Tick what the property offers. Shown as chips on cards and the docket."
    >
      <UzFormField name="amenities">
        {({ field }) => (
          <AmenityGrid
            value={(field.value as string[]) ?? []}
            onChange={(next) => field.onChange(next)}
            disabled={pending}
          />
        )}
      </UzFormField>
    </Card>
    <Card
      title="Photos"
      description="Uploaded straight to storage - same flow as custom pages. The cover fronts every card."
    >
      <UzFormField name="photos">
        {({ field }) => (
          <HotelPhotosFields
            photos={(field.value as HotelPhoto[]) ?? []}
            onChange={(next) => field.onChange(next)}
            disabled={pending}
          />
        )}
      </UzFormField>
    </Card>
  </>
);

const PoliciesCard = ({ pending }: CardProps): React.JSX.Element => (
  <Card title="Policies & timings">
    <div className="grid gap-5 sm:grid-cols-2">
      <UzFormField name="checkInTime">
        {({ field, error }) => (
          <UzDateTimePicker
            mode="time"
            hourFormat="24"
            label="Check-in time"
            value={hhmmToDate(field.value as string | null | undefined)}
            onValueChange={(d) => field.onChange(dateToHhmm(d))}
            error={error}
            disabled={pending}
          />
        )}
      </UzFormField>
      <UzFormField name="checkOutTime">
        {({ field, error }) => (
          <UzDateTimePicker
            mode="time"
            hourFormat="24"
            label="Check-out time"
            value={hhmmToDate(field.value as string | null | undefined)}
            onValueChange={(d) => field.onChange(dateToHhmm(d))}
            error={error}
            disabled={pending}
          />
        )}
      </UzFormField>
    </div>
    <UzFormField name="policiesHtml">
      {({ field, error }) => (
        <UzTextarea
          {...field}
          value={(field.value as string | null | undefined) ?? ''}
          onChange={(e) => field.onChange(e.target.value === '' ? null : e.target.value)}
          label="Policies"
          placeholder="Cancellation, pet policy, child policy, ..."
          rows={5}
          error={error}
          disabled={pending}
        />
      )}
    </UzFormField>
  </Card>
);

// ---------------------------------------------------------------------------
// Sidebar cards
// ---------------------------------------------------------------------------

interface StatusCardProps extends CardProps {
  canApprove: boolean;
}

const StatusCard = ({ pending, canApprove }: StatusCardProps): React.JSX.Element => (
  <Card title="Status">
    <UzFormField name="status">
      {({ field, error }) => (
        <UzSelect
          name={field.name}
          label="Lifecycle"
          options={
            canApprove
              ? STATUS_OPTIONS
              : STATUS_OPTIONS.filter((o) => o.value === 'DRAFT' || o.value === 'PENDING_APPROVAL')
          }
          value={(field.value as string | undefined) || 'DRAFT'}
          onValueChange={(v) => field.onChange(v)}
          error={error}
          disabled={pending}
        />
      )}
    </UzFormField>
  </Card>
);

const CommercialCard = ({ form, pending }: FormCardProps): React.JSX.Element => {
  const markupKind = form.watch('markupKind');
  return (
    <Card title="Commercial">
      <UzFormField name="vendorSupplierId">
        {({ field, error }) => (
          <SupplierPicker
            value={(field.value as string | null | undefined) ?? null}
            onValueChange={(v) => field.onChange(v)}
            disabled={pending}
            error={error}
          />
        )}
      </UzFormField>
      <UzFormField name="contractRef">
        {({ field, error }) => (
          <UzInput
            {...field}
            value={(field.value as string | null | undefined) ?? ''}
            onChange={(e) => field.onChange(e.target.value === '' ? null : e.target.value)}
            label="Contract reference"
            placeholder="e.g. C-2026-001"
            error={error}
            disabled={pending}
          />
        )}
      </UzFormField>
      <UzFormField name="markupKind">
        {({ field, error }) => (
          <UzSelect
            name={field.name}
            label="Markup type"
            options={MARKUP_OPTIONS}
            value={(field.value as string | null | undefined) ?? ''}
            onValueChange={(v) => field.onChange(v ? (v as 'PERCENT' | 'FLAT') : null)}
            error={error}
            disabled={pending}
          />
        )}
      </UzFormField>
      {markupKind && (
        <UzFormField name="markupValue">
          {({ field, error }) => (
            <UzNumberInput
              name={field.name}
              label={markupKind === 'PERCENT' ? 'Markup %' : 'Markup amount'}
              precision={2}
              min={0}
              leftAdornment={
                markupKind === 'FLAT' ? <span className="text-sm text-zinc-500">₹</span> : undefined
              }
              rightAdornment={
                markupKind === 'PERCENT' ? (
                  <span className="text-sm text-zinc-500">%</span>
                ) : undefined
              }
              value={(field.value as number | undefined) || ''}
              onValueChange={(v) => field.onChange(v ?? 0)}
              hint={
                markupKind === 'PERCENT'
                  ? 'Applied on the net rate at quote time'
                  : 'Flat amount added per quote'
              }
              error={error}
              disabled={pending}
            />
          )}
        </UzFormField>
      )}
    </Card>
  );
};

const ConnectionsCard = ({
  pending,
  hotel,
}: CardProps & { hotel: HotelDetail | null }): React.JSX.Element => (
  <Card title="Connections">
    <UzFormField name="tboHotelCode">
      {({ field, error }) => (
        <UzInput
          {...field}
          value={(field.value as string | null | undefined) ?? ''}
          onChange={(e) => field.onChange(e.target.value === '' ? null : e.target.value)}
          label="TBO hotel code"
          hint="Links to a TBO mirror row for picker dedup."
          error={error}
          disabled={pending}
        />
      )}
    </UzFormField>
    {hotel && (hotel.legacyId !== null || hotel.legacyActivityName) && (
      <div className="space-y-1 text-xs text-zinc-500">
        {hotel.legacyId !== null && (
          <p>
            Legacy <span className="font-mono text-zinc-700 tabular-nums">#{hotel.legacyId}</span>
          </p>
        )}
        {hotel.legacyActivityName && hotel.legacyActivityName !== hotel.name && (
          <p>
            Promoted from <span className="text-zinc-700">{hotel.legacyActivityName}</span>
          </p>
        )}
      </div>
    )}
  </Card>
);
